import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../contexts/ProjectContext';
import { getAllSubElements, getSubElementPath } from '../../data/mockData';
import { useClickOutside } from '../../hooks/useClickOutside';
import { SearchIcon } from '../icons/Icons';
import {
  Wrench, FolderOpen, Layers, Package, User, FolderKanban, X,
} from 'lucide-react';
import './GlobalSearch.css';

type ResultKind = 'task' | 'zone' | 'section' | 'element' | 'member' | 'project';

// Fixed display order of result groups. Module-scoped so its identity is stable
// across renders (lets the flatResults memo depend only on `results`).
const ORDER: ResultKind[] = ['task', 'zone', 'section', 'element', 'member', 'project'];

interface SearchResult {
  kind: ResultKind;
  id: string;
  primary: string;
  secondary?: string;
  breadcrumb?: string;
  badge?: string;
  badgeColor?: string;
  projectId?: string;      // owning project (for cross-project navigation)
}

const STATUS_COLORS: Record<string, string> = {
  ASSIGNED:    '#6b7280',
  STARTED:     '#3b82f6',
  IN_PROGRESS: '#28509e',
  PAUSED:      '#f59e0b',
  FINISHED:    '#22c55e',
  ISSUES:      '#ef4444',
};

function KindIcon({ kind }: { kind: ResultKind }) {
  const props = { size: 14, strokeWidth: 1.8 };
  switch (kind) {
    case 'task':    return <Wrench       {...props} />;
    case 'zone':    return <FolderOpen   {...props} />;
    case 'section': return <Layers       {...props} />;
    case 'element': return <Package      {...props} />;
    case 'member':  return <User         {...props} />;
    case 'project': return <FolderKanban {...props} />;
  }
}

const GROUP_LABELS: Record<ResultKind, string> = {
  task:    'Tasks',
  zone:    'Zones',
  section: 'Sections',
  element: 'Elements',
  member:  'Members',
  project: 'Projects',
};

const MAX_PER_GROUP = 5;

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { currentUser, isProjectManager } = useAuth();
  const { activeProject, projects, activeProjectMembers, setActiveProjectId } = useProjects();

  useClickOutside(wrapperRef, () => setIsOpen(false), isOpen);

  const results = useMemo((): Record<ResultKind, SearchResult[]> | null => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;

    const match = (s?: string | null) => !!s && s.toLowerCase().includes(q);

    // Projects the current user can see (PM: all; worker: only their memberships).
    const visibleProjects = isProjectManager
      ? projects
      : projects.filter(p => currentUser && p.teamMembers.includes(currentUser.id));

    // Tasks across ALL visible projects, tagged with their owning project so
    // selecting one from another project switches to it before navigating.
    const taskMatches = visibleProjects.flatMap(project =>
      getAllSubElements(project)
        .filter(t => isProjectManager || (currentUser && t.assignedTo.includes(currentUser.id)))
        .filter(t => match(t.name) || match(t.code) || match(t.description) || match(t.status) || match(t.priority))
        .map(t => ({ task: t, project }))
    );
    const tasks: SearchResult[] = taskMatches
      .slice(0, MAX_PER_GROUP)
      .map(({ task: t, project }) => {
        const path = getSubElementPath(project, t.id);
        const crumbBase = path ? `${path.zone.code} › ${path.section.code}` : '';
        const breadcrumb = project.id === activeProject?.id ? crumbBase : `${project.code} · ${crumbBase}`;
        return {
          kind: 'task',
          id: t.id,
          projectId: project.id,
          primary: t.code || t.name,
          secondary: t.name !== t.code ? t.name : undefined,
          breadcrumb,
          badge: t.status.replace('_', ' '),
          badgeColor: STATUS_COLORS[t.status] ?? '#6b7280',
        };
      });

    if (!isProjectManager) {
      return { task: tasks, zone: [], section: [], element: [], member: [], project: [] };
    }

    const zones: SearchResult[] = visibleProjects
      .flatMap(project => project.zones.map(z => ({ ...z, projectId: project.id, projectCode: project.code })))
      .filter(z => match(z.name) || match(z.code) || match(z.description))
      .slice(0, MAX_PER_GROUP)
      .map(z => ({ kind: 'zone', id: z.id, projectId: z.projectId, primary: z.name, secondary: z.code, breadcrumb: z.projectId === activeProject?.id ? undefined : z.projectCode }));

    const sections: SearchResult[] = visibleProjects
      .flatMap(project => project.zones.flatMap(z => z.sections.map(s => ({ ...s, zoneName: z.name, projectId: project.id, projectCode: project.code }))))
      .filter(s => match(s.name) || match(s.code) || match(s.description))
      .slice(0, MAX_PER_GROUP)
      .map(s => ({ kind: 'section', id: s.id, projectId: s.projectId, primary: s.name, breadcrumb: s.projectId === activeProject?.id ? s.zoneName : `${s.projectCode} · ${s.zoneName}` }));

    const elements: SearchResult[] = visibleProjects
      .flatMap(project => project.zones.flatMap(z => z.sections.flatMap(s => s.elements.map(e => ({ ...e, sectionName: s.name, projectId: project.id, projectCode: project.code })))))
      .filter(e => match(e.name) || match(e.code) || match(e.description))
      .slice(0, MAX_PER_GROUP)
      .map(e => ({ kind: 'element', id: e.id, projectId: e.projectId, primary: e.name, breadcrumb: e.projectId === activeProject?.id ? e.sectionName : `${e.projectCode} · ${e.sectionName}` }));

    const members: SearchResult[] = activeProjectMembers
      .filter(m => match(m.name) || match(m.email) || match(m.role))
      .slice(0, MAX_PER_GROUP)
      .map(m => ({ kind: 'member', id: m.id, primary: m.name, badge: m.role.replace('_', ' ') }));

    const projs: SearchResult[] = projects
      .filter(p => match(p.name) || match(p.code) || match(p.client))
      .slice(0, MAX_PER_GROUP)
      .map(p => ({
        kind: 'project',
        id: p.id,
        primary: p.name,
        secondary: p.client,
        badge: p.status.replace('_', ' '),
      }));

    return { task: tasks, zone: zones, section: sections, element: elements, member: members, project: projs };
  }, [query, activeProject, projects, activeProjectMembers, currentUser, isProjectManager]);

  const flatResults = useMemo((): SearchResult[] => {
    if (!results) return [];
    return ORDER.flatMap(k => results[k]);
  }, [results]);

  const hasAnyResults = flatResults.length > 0;

  const handleSelect = useCallback((item: SearchResult) => {
    // Selecting a result from another project switches the active project first.
    if (item.projectId && item.projectId !== activeProject?.id) {
      setActiveProjectId(item.projectId);
    }
    switch (item.kind) {
      case 'task':
        navigate(isProjectManager ? `/workspace/${item.id}` : `/my-tasks/${item.id}`);
        break;
      case 'zone':
      case 'section':
      case 'element':
        // Pass the target so Workspace expands its ancestor chain, scrolls to it
        // and highlights it — instead of dropping the user at the top blindly.
        navigate('/workspace', { state: { focusNode: { kind: item.kind, id: item.id } } });
        break;
      case 'member':
        navigate('/team');
        break;
      case 'project':
        setActiveProjectId(item.id);
        navigate('/');
        break;
    }
    setQuery('');
    setIsOpen(false);
    setFocusedIndex(-1);
  }, [navigate, isProjectManager, setActiveProjectId, activeProject?.id]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || !results) return;
    if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleSelect(flatResults[focusedIndex]);
    }
  }, [isOpen, results, flatResults, focusedIndex, handleSelect]);

  useEffect(() => { setFocusedIndex(-1); }, [query]);

  const showDropdown = isOpen && query.trim().length >= 2;

  let flatIdx = 0;

  return (
    <div className="gs-wrapper" ref={wrapperRef}>
      <div className={`gs-input-wrap ${isOpen ? 'focused' : ''}`}>
        <SearchIcon size={18} />
        <input
          ref={inputRef}
          type="search"
          className="gs-input"
          placeholder="Search project or task..."
          aria-label="Search project or task"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {query && (
          <button
            className="gs-clear"
            type="button"
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            tabIndex={-1}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="gs-dropdown" role="listbox">
          {!hasAnyResults ? (
            <p className="gs-empty">No results for "{query}"</p>
          ) : (
            ORDER.map(kind => {
              const group = results![kind];
              if (!group.length) return null;
              return (
                <div key={kind} className="gs-group">
                  <p className="gs-group-label">{GROUP_LABELS[kind]}</p>
                  {group.map(item => {
                    const idx = flatIdx++;
                    return (
                      <div
                        key={item.id}
                        role="option"
                        aria-selected={focusedIndex === idx}
                        className={`gs-item${focusedIndex === idx ? ' focused' : ''}`}
                        onMouseDown={() => handleSelect(item)}
                        onMouseEnter={() => setFocusedIndex(idx)}
                      >
                        <span className="gs-kind-icon">
                          <KindIcon kind={kind} />
                        </span>
                        <div className="gs-item-body">
                          <span className="gs-item-primary">{item.primary}</span>
                          {item.secondary && <span className="gs-item-secondary">{item.secondary}</span>}
                          {item.breadcrumb && <span className="gs-breadcrumb">{item.breadcrumb}</span>}
                        </div>
                        {item.badge && (
                          <span
                            className="gs-badge"
                            style={{ background: item.badgeColor ? `${item.badgeColor}22` : undefined, color: item.badgeColor }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
